import { Check } from "lucide-react";
import { FOOTER_SOCIAL_LINKS } from "@/components/ui/SocialLinks";
import { Footer } from "@/components/ui/footer";

import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { createIsomorphicFn } from "@tanstack/react-start";
import { PulseFitHero } from "@/components/ui/pulse-fit-hero";
import { ShareloEmbed } from "@/components/ui/ShareloEmbed";

import { projectsQueryOptions } from "@/lib/portfolio";

import { TestimonialFeatureSection } from "@/components/sections/TestimonialFeatureSection";
import { AboutSection } from "@/components/sections/AboutSection";
import { BrandHeritageSection } from "@/components/sections/BrandHeritageSection";
import { TestimonialFeatureSectionThree } from "@/components/sections/TestimonialFeatureSectionThree";
import { WorkMarquee } from "@/components/portfolio/WorkMarquee";
import { ReadyToGrowSection } from "@/components/sections/ReadyToGrowSection";
import { PricingSection } from "@/components/sections/PricingSection";
import { FaqSection } from "@/components/sections/FaqSection";
import { ContactSection } from "@/components/sections/ContactSection";

import logo from "@/assets/mind-media-logo.svg";

import heroVideoPoster from "@/assets/hero-video-poster.jpg.asset.json";

import { trustAvatars } from "@/components/sections/trust-avatars";

const setEdgeCache = createIsomorphicFn()
  .server(async () => {
    const { setHtmlEdgeCache } = await import("@/lib/edge-cache.server");
    setHtmlEdgeCache();
  })
  .client(() => {});

export const Route = createFileRoute("/homepage-archive-original")({
  head: () => ({
    meta: [
      { title: "Archived Homepage — Mind Media" },
      {
        name: "description",
        content: "Archived version of the previous Mind Media homepage. Not indexed.",
      },
      { name: "robots", content: "noindex, nofollow" },
      { property: "og:title", content: "Archived Homepage — Mind Media" },
      {
        property: "og:description",
        content: "Archived version of the previous Mind Media homepage.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
  }),
  loader: ({ context }) => {
    setEdgeCache();
    return context.queryClient.ensureQueryData(projectsQueryOptions);
  },
  component: ArchivedHomepage,
});

function ArchivedHomepage() {
  const navigate = useNavigate();
  const goToMockup = () => navigate({ to: "/get-mockup" });

  return (
    <main className="cta-black min-h-screen bg-background">
      <PulseFitHero
        logo={<img src={logo} alt="Mind Media" className="h-7 w-auto" />}
        navigation={[
          { label: "Work", href: "#work" },
          { label: "About", href: "#about" },
          { label: "Services", href: "#pricing" },
          { label: "Pricing", href: "#pricing" },
          { label: "FAQ", href: "#faq" },
          { label: "Contact", href: "#contact" },
        ]}
        ctaButton={{ label: "Book a Meeting", href: "#contact" }}
        title="Stop overpaying for websites that don't work."
        subtitle="Built by a business owner who got sick of agencies charging thousands for slow, templated garbage. I deliver stunning modern websites - built for leads and sales."
        primaryAction={{ label: "Get a Free Mockup", onClick: goToMockup }}
        secondaryAction={{ label: "Pricing", href: "#pricing" }}
        disclaimer={
          <div className="mx-auto mt-2 flex w-full flex-col items-start justify-start gap-y-2 sm:flex-row sm:flex-nowrap sm:items-center sm:justify-center sm:gap-x-4 sm:gap-y-0 sm:overflow-x-auto lg:mx-0 lg:justify-start">
            {["Free 30-min Strategy Session", "100% Money Back Guarantee", "No Commitment"].map(
              (item) => (
                <div key={item} className="flex shrink-0 items-center gap-2 whitespace-nowrap text-sm text-foreground">
                  <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full border border-brand-green bg-white">
                    <Check className="h-3 w-3 text-brand-green" strokeWidth={3} />
                  </span>
                  <span className="text-sm font-medium text-gray-950 xl:text-base">{item}</span>
                </div>
              ),
            )}
          </div>
        }
        socialProof={{ avatars: trustAvatars }}
        showBrands
        media={
          <ShareloEmbed
            id="4XbnluOZ4qbUOqP2ig9X5"
            title="Why us — customer story"
            preload
            poster={heroVideoPoster.url}
          />
        }
      />

      <WorkMarquee />
      <TestimonialFeatureSection />
      <PricingSection onCtaClick={goToMockup} />

      <AboutSection onCtaClick={goToMockup} />
      <BrandHeritageSection
        onCtaClick={goToMockup}
        className="pb-0 sm:pb-0"
        showDivider={false}
      />
      <TestimonialFeatureSectionThree />
      <ReadyToGrowSection onCtaClick={goToMockup} />
      <FaqSection />
      <ContactSection />

      <Footer
        logo={<img src={logo} alt="Mind Media" className="h-7 w-auto" />}
        brandName="Mind Media"
        socialLinks={FOOTER_SOCIAL_LINKS}
        mainLinks={[
          { href: "/the-problem", label: "The Problem" },
          { href: "#work", label: "Work" },
          { href: "#pricing", label: "Pricing" },
          { href: "#faq", label: "FAQ" },
          { href: "/blog", label: "Blog" },
          { href: "/services", label: "Services" },
        ]}
        legalLinks={[
          { href: "/privacy", label: "Privacy" },
          { href: "/terms", label: "Terms" },
        ]}
        copyright={{
          text: `© ${new Date().getFullYear()} Mind Media`,
          license: "All rights reserved",
        }}
      />
    </main>
  );
}
